import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Avatar,
  Typography,
  Space,
  Button,
  Drawer,
  Form,
  Input,
  message,
  Progress,
  Image,
  Select,
  Tag,
} from "antd";
import {
  getListProduct,
  createProduct,
  getProductToSync,
  updateProductVariant,
  getProductTags,
  getProductBrands,
  getProductCategories,
} from "services/product";
import { Link } from "react-router-dom";
import { getDatabase, getDataFromAccurate } from "services/accurate";
import { SearchOutlined } from "@ant-design/icons";

const { Title } = Typography;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [productBrands, setProductBrands] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [dbAccurate, setDBAccurate] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalData, setTotalData] = useState(0);

  const [syncProgress, setSyncProgress] = useState({
    percent: 0,
    status: "active",
    message: "",
  });
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [loadingButtonSync, setLoadingButtonSync] = useState(false);

  const processedCountRef = useRef(0);
  const searchTimeoutRef = useRef(null);

  const [filters, setFilters] = useState({
    status: "All",
    brand: "All",
    category: "All",
    tag: "All",
    search: "",
  });

  // ========================= FETCH PRODUCTS =========================
  const fetchProducts = async (page = 1, limit = 10, f = filters) => {
    try {
      // bangun query params dinamis
      const params = { page, limit };

      if (f.status && f.status !== "All") params.status = f.status;
      if (f.brand && f.brand !== "All") params.brand = f.brand;
      if (f.category && f.category !== "All") params.category = f.category;
      if (f.tag && f.tag !== "All") params.tag = f.tag;
      if (f.search) params.search = f.search;

      const response = await getListProduct(params);

      if (response?.result) {
        setProducts(response.result);
        setTotalData(response.pagination.total_data);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  // ========================= FETCH FILTER DATA =========================
  const fetchProductTags = async () => {
    try {
      const response = await getProductTags();
      if (response?.result) setProductTags(response.result);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProductBrands = async () => {
    try {
      const response = await getProductBrands();
      if (response?.result) setProductBrands(response.result);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProductCategories = async () => {
    try {
      const response = await getProductCategories();
      if (response?.result) setProductCategories(response.result);
    } catch (err) {
      console.error(err);
    }
  };

  const getDBAccurate = async () => {
    try {
      const response = await getDatabase();
      if (response?.data) {
        const db = response.data.find((x) =>
          x.dbname.toLowerCase().includes("mitra")
        );
        setDBAccurate(db);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ========================= SYNC PRODUCT =========================
  const getSync = useCallback(
    async (currentVariant, totalVariant) => {
      if (!dbAccurate) return;

      const { token, host, session } = dbAccurate;

      const requestBody = {
        session,
        token,
        api_url: `${host}/accurate/api/item/detail.do?id=${currentVariant.item_id}`,
      };

      try {
        const response = await getDataFromAccurate(requestBody);
        const data = response.d;

        await updateProductVariant({
          ...currentVariant,
          price: data.unitPrice,
          item_no: data.no,
          product_name: data.name,
        });

        processedCountRef.current += 1;
        const newPercent = Math.round(
          (processedCountRef.current / totalVariant) * 100
        );
        setSyncProgress({
          percent: newPercent > 100 ? 100 : newPercent,
          status: "active",
          message: `Syncing: ${data.no}`,
        });
      } catch (error) {
        console.error(error);
        setSyncProgress({
          percent: 100,
          status: "exception",
          message: "Sync failed!",
        });
        setLoadingButtonSync(false);
      }
    },
    [dbAccurate]
  );

  const prepareSync = useCallback(async () => {
    try {
      setShowProgressBar(true);
      setSyncProgress({
        percent: 0,
        status: "active",
        message: "Fetching total customer count...",
      });

      const response = await getProductToSync();
      if (response?.result) {
        const datas = response.result;
        setSyncProgress({
          percent: 0,
          status: "active",
          message: `Starting sync of ${datas.length} product variant...`,
        });

        for (let i = 0; i <= datas.length - 1; i++) {
          await getSync(datas[i], datas.length);
        }

        setSyncProgress({
          percent: 100,
          status: "success",
          message: "Sync complete!",
        });
        setLoadingButtonSync(false);
        setTimeout(() => setShowProgressBar(false), 2000);
      }
    } catch (err) {
      console.error(err);
    }
  }, [getSync]);

  // ========================= HANDLERS =========================
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value }));
    }, 500);
  };

  const handleSubmit = async (values) => {
    await createProduct(values);
    message.success("Product created successfully!");
    fetchProducts(currentPage, pageSize);
    onClose();
  };

  const showDrawer = () => setVisible(true);
  const onClose = () => {
    setVisible(false);
    form.resetFields();
  };

  // ========================= EFFECTS =========================
  useEffect(() => {
    getDBAccurate();
    fetchProductTags();
    fetchProductBrands();
    fetchProductCategories();
    fetchProducts(1, pageSize, filters);
  }, []);

  useEffect(() => {
    fetchProducts(currentPage, pageSize, filters);
  }, [filters, currentPage, pageSize]);

  // ========================= TABLE DATA =========================
  const columns = [
    { title: "Product", dataIndex: "name", key: "name", width: "40%" },
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Tag", dataIndex: "tag", key: "tag" },
    { title: "Price", dataIndex: "price", key: "price" },
    { title: "Status", dataIndex: "is_promo", key: "is_promo" },
    { title: "Action", dataIndex: "action", key: "action" },
  ];

  const tableData = products.map(
    ({
      brand,
      category,
      id,
      image_url,
      name,
      price,
      tag,
      is_promo,
      is_bestselling,
    }) => ({
      key: id,
      name: (
        <Avatar.Group>
          <Image
            width={50}
            src={image_url ?? "https://via.placeholder.com/40"}
            alt="image"
            style={{ borderRadius: 8 }}
          />
          <div className="avatar-info" style={{ marginLeft: 12 }}>
            <Title level={5}>{name}</Title>
            <p>{brand}</p>
          </div>
        </Avatar.Group>
      ),
      category: <div className="text-sm">{category}</div>,
      tag: <div className="text-sm">{tag || "-"}</div>,

      price: <div className="text-sm">{price.toLocaleString()}</div>,
      is_promo: (
        <>
          {is_promo == "1" && <Tag color="green">Promo</Tag>}{" "}
          {is_bestselling == "1" && <Tag color="geekblue">Best Selling</Tag>}
        </>
      ),
      action: (
        <Link to={`/ecommerce/product/detail/${id}`} className="text-blue-600">
          Detail
        </Link>
      ),
    })
  );

  // ========================= RENDER =========================
  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Product"
              extra={
                <Space>
                  <Button type="primary" onClick={showDrawer}>
                    Add Product
                  </Button>
                  <Button
                    type="danger"
                    onClick={prepareSync}
                    loading={loadingButtonSync}
                  >
                    Sync With Accurate
                  </Button>
                </Space>
              }
            >
              {showProgressBar && (
                <div style={{ padding: "24px" }}>
                  <Progress
                    percent={syncProgress.percent}
                    status={syncProgress.status}
                    strokeColor={
                      syncProgress.status === "success"
                        ? "#52c41a"
                        : syncProgress.status === "exception"
                        ? "#ff4d4f"
                        : "#1890ff"
                    }
                  />
                  <div style={{ textAlign: "center", marginTop: "8px" }}>
                    {syncProgress.message}
                  </div>
                </div>
              )}

              <div style={{ padding: "10px" }}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search Product"
                  allowClear
                  bordered={false}
                  onChange={handleSearchChange}
                />
              </div>

              <Space size={15} style={{ paddingLeft: "30px" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: 4 }}>Product Status:</label>
                  <Select
                    value={filters.status}
                    style={{ width: 200 }}
                    onChange={(val) => handleFilterChange("status", val)}
                    options={[
                      { value: "All", label: "All" },
                      { value: "promo", label: "Promo" },
                      { value: "best", label: "Best Selling" },
                    ]}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: 4 }}>Brand:</label>
                  <Select
                    value={filters.brand}
                    style={{ width: 200 }}
                    onChange={(val) => handleFilterChange("brand", val)}
                    options={productBrands.map((x) => ({
                      label: x.brand,
                      value: x.brand,
                    }))}
                    allowClear={true}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: 4 }}>Category:</label>
                  <Select
                    value={filters.category}
                    style={{ width: 200 }}
                    onChange={(val) => handleFilterChange("category", val)}
                    options={productCategories.map((x) => ({
                      label: x.category,
                      value: x.category,
                    }))}
                    allowClear={true}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: 4 }}>Tag:</label>
                  <Select
                    value={filters.tag}
                    style={{ width: 200 }}
                    onChange={(val) => handleFilterChange("tag", val)}
                    options={productTags.map((x) => ({
                      label: x.tag,
                      value: x.tag,
                    }))}
                    allowClear={true}
                  />
                </div>
              </Space>

              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={tableData}
                  className="ant-border-space"
                  pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalData,
                    showSizeChanger: true,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Drawer for Create Product */}
      <Drawer
        title="Add Product"
        width={400}
        visible={visible}
        onClose={onClose}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input placeholder="Product name" />
          </Form.Item>

          <Form.Item label="Brand" name="brand" rules={[{ required: true }]}>
            <Input placeholder="Brand" />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true }]}
          >
            <Input placeholder="Category" />
          </Form.Item>

          <Form.Item label="Tag" name="tag">
            <Input placeholder="Tag" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={6} placeholder="Product description" />
          </Form.Item>

          <Form.Item>
            <Space style={{ float: "right" }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}
